import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserRound } from "lucide-react";
import type { BuildLookupRow, GroupWithMembers, MemberDraft, MemberWithRatings } from "../types";
import { getApi } from "../api";
import { MAX_WEIGHT_LBS, buildPoints, isUnrealisticHeight, isUnrealisticWeight } from "../calc";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { useEnsureGroup } from "../hooks/useEnsureGroup";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { selectMember, useGroupStore } from "../store/groupStore";
import { confirmAction, notify, useUiStore } from "../store/uiStore";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { ErrorState, SkeletonForm } from "../components/ScreenStates";
import { MemberFormFields, type MemberFormField, type MemberFormValues } from "../components/member/MemberFormFields";
import { GRADIENT_DOE_EXISTS_TOOLTIP, gradientDoeDraft } from "../utils/gradientDoe";
import { fullName } from "../utils/format";
import { hasErrors, isoDate, numberBetween, postalCode, required, ssn, validateFields, type FieldErrors } from "../validation/rules";

interface MemberFormPageProps {
  mode: "add" | "edit";
}

const EMPTY: MemberFormValues = { firstName: "", lastName: "", relation: null, subscriberMemberId: "", gender: null, dateOfBirth: "", heightFeet: null, heightInches: null, weightLbs: null, postalCode: "", ssn: "", notes: "", isExcluded: false };

function valuesFrom(member: MemberWithRatings | null): MemberFormValues {
  if (!member) return EMPTY;
  return { firstName: member.firstName, lastName: member.lastName, relation: member.relation, subscriberMemberId: member.subscriberMemberId ?? "", gender: member.gender ?? "", dateOfBirth: member.dateOfBirth ?? "", heightFeet: member.heightFeet, heightInches: member.heightInches, weightLbs: member.weightLbs, postalCode: member.postalCode ?? "", ssn: "", notes: member.notes ?? "", isExcluded: member.isExcluded };
}

function toMemberDraft(values: MemberFormValues, memberType: MemberDraft["memberType"]): MemberDraft {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    relation: values.relation ?? "Employee",
    subscriberMemberId: values.relation === "Employee" ? null : values.subscriberMemberId || null,
    gender: values.gender === "M" || values.gender === "F" ? values.gender : null,
    dateOfBirth: values.dateOfBirth || null,
    heightFeet: values.heightFeet,
    heightInches: values.heightInches,
    weightLbs: values.weightLbs,
    postalCode: values.postalCode.trim() || null,
    ssn: values.ssn || null,
    notes: values.notes.trim() || null,
    memberType,
  };
}

function rulesFor(needsSubscriber: boolean, hasSubscriberCandidates: boolean) {
  return {
    firstName: [required("First Name is required")],
    lastName: [required("Last Name is required")],
    relation: [required("Relation is required")],
    subscriberMemberId: needsSubscriber ? [required(hasSubscriberCandidates ? "Choose the subscriber for this dependent" : "No employee is available to be this dependent's subscriber")] : [],
    gender: [],
    dateOfBirth: [isoDate()],
    heightFeet: [numberBetween(0, 9)],
    heightInches: [numberBetween(0, 11)],
    weightLbs: [numberBetween(0, MAX_WEIGHT_LBS)],
    postalCode: [postalCode()],
    ssn: [ssn()],
    notes: [],
    isExcluded: [],
  };
}

interface MemberFormProps {
  group: GroupWithMembers;
  member: MemberWithRatings | null;
  buildTable: readonly BuildLookupRow[] | null;
}

function MemberForm({ group, member, buildTable }: MemberFormProps) {
  const navigate = useNavigate();
  const saveMember = useGroupStore((state) => state.saveMember);
  const toggleMemberExcluded = useGroupStore((state) => state.toggleMemberExcluded);
  const setHighlightedMemberId = useUiStore((state) => state.setHighlightedMemberId);
  const [values, setValues] = useState<MemberFormValues>(() => valuesFrom(member));
  const [errors, setErrors] = useState<FieldErrors<MemberFormField>>({});
  const [isSaving, setIsSaving] = useState(false);
  const memberType = member?.memberType ?? "Real";
  const employees = useMemo(() => group.members.filter((candidate) => candidate.relation === "Employee" && candidate.memberType === "Real" && candidate.id !== member?.id), [group, member]);
  const subscriberOptions = useMemo(() => [{ value: "", label: "Select a subscriber" }, ...employees.map((employee) => ({ value: employee.id, label: fullName(employee.firstName, employee.lastName) }))], [employees]);
  // A dependent always needs a subscriber; with no other employee in the group the select shows the gap instead of hiding it.
  const needsSubscriber = values.relation !== null && values.relation !== "Employee";
  const hasSubscriberCandidates = employees.length > 0;
  const hasGradientDoe = group.members.some((candidate) => candidate.memberType === "GradientDoe");
  const demographics = { gender: values.gender === "M" || values.gender === "F" ? values.gender : null, dateOfBirth: values.dateOfBirth || null, heightFeet: values.heightFeet, heightInches: values.heightInches, weightLbs: values.weightLbs };
  const previewBuild = buildTable ? buildPoints(demographics, group.effectiveDate, buildTable) : null;

  const finish = useCallback(
    (savedId: string, message: string) => {
      setHighlightedMemberId(savedId);
      notify(message, "success");
      navigate(`/uw/groups/${group.id}`);
    },
    [setHighlightedMemberId, navigate, group.id],
  );

  const handleSave = useCallback(async () => {
    const nextErrors = validateFields<MemberFormField>(values, rulesFor(needsSubscriber, hasSubscriberCandidates));
    setErrors(nextErrors);
    if (hasErrors(nextErrors)) return;
    setIsSaving(true);
    const saved = await saveMember(member?.id ?? null, toMemberDraft(values, memberType));
    if (saved && values.isExcluded !== saved.isExcluded) {
      const toggled = await toggleMemberExcluded(saved.id, values.isExcluded);
      if (!toggled) {
        setIsSaving(false);
        return;
      }
      for (const cohortId of toggled.autoExcludedSpockMemberIds) {
        const cohort = toggled.detail.group.members.find((candidate) => candidate.id === cohortId);
        if (cohort) notify(`Spock ${cohort.cohortYear} ${cohort.cohortGender} cohort was excluded because no real member with that birth year and sex remains.`, "info");
      }
    }
    setIsSaving(false);
    if (saved) finish(saved.id, `${member ? "Saved" : "Added"} ${fullName(saved.firstName, saved.lastName)}.`);
  }, [values, needsSubscriber, hasSubscriberCandidates, saveMember, toggleMemberExcluded, member, memberType, finish]);

  const handleGradientDoe = async () => {
    const confirmed = await confirmAction({ title: "Add Gradient Doe", message: "Add the fixed Gradient Doe member (Employee, F, born 10/23/1825, 5'6\", 150 lbs)? It saves immediately.", confirmLabel: "Add Gradient Doe", variant: "primary" });
    if (!confirmed) return;
    setIsSaving(true);
    const saved = await saveMember(null, gradientDoeDraft());
    setIsSaving(false);
    if (saved) finish(saved.id, "Added Gradient Doe.");
  };

  const handleCancel = useCallback(() => navigate(`/uw/groups/${group.id}`), [navigate, group.id]);
  const shortcuts = useMemo(() => ({ save: () => void handleSave(), cancel: handleCancel }), [handleSave, handleCancel]);
  useShortcuts(shortcuts);

  return (
    <>
      <PageHeader
        title={member ? `Edit ${fullName(member.firstName, member.lastName)}` : "Add Member"}
        subtitle={group.name}
        actions={
          !member ? (
            <Button variant="secondary" onClick={() => void handleGradientDoe()} disabled={isSaving || hasGradientDoe} title={hasGradientDoe ? GRADIENT_DOE_EXISTS_TOOLTIP : undefined}>
              Add Gradient Doe
            </Button>
          ) : undefined
        }
      />
      <div className="mx-auto max-w-[720px] space-y-5">
        <Card title="Member" icon={UserRound}>
          <MemberFormFields
            values={values}
            errors={errors}
            onChange={setValues}
            subscriberOptions={subscriberOptions}
            needsSubscriber={needsSubscriber}
            hasSubscriberCandidates={hasSubscriberCandidates}
            storedSsnMasked={member?.ssnMasked ?? null}
            previewBuild={previewBuild}
            heightWarning={isUnrealisticHeight(values.heightFeet, values.heightInches)}
            weightWarning={isUnrealisticWeight(values.weightLbs)}
          />
        </Card>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} isLoading={isSaving}>
            Save Member
          </Button>
        </div>
      </div>
    </>
  );
}

export function MemberFormPage({ mode }: MemberFormPageProps) {
  const { groupId = "", memberId } = useParams();
  const ensure = useEnsureGroup(groupId);
  const group = useGroupStore((state) => (state.group?.id === groupId ? state.group : null));
  const member = useGroupStore(selectMember(memberId));
  const buildTable = useAsyncResource(() => getApi().getBuildTable(), "build-table", "MemberFormPage.buildTable");
  const title = mode === "add" ? "Add Member" : `Edit ${member ? fullName(member.firstName, member.lastName) : "Member"}`;
  const breadcrumb = useMemo(() => (group ? [{ label: "Recent Groups", to: "/uw/recent" }, { label: group.name, to: `/uw/groups/${group.id}`, status: group.status }, { label: title, to: "" }] : null), [group, title]);
  usePageSetup(title, breadcrumb);

  if (ensure.error) return <ErrorState message={ensure.error} onRetry={ensure.retry} />;
  if (!group || (mode === "edit" && !member)) return ensure.isLoading ? <SkeletonForm fields={8} /> : <ErrorState message="Member not found in this group." onRetry={ensure.retry} />;

  return <MemberForm key={`${group.id}-${member?.id ?? "new"}`} group={group} member={mode === "edit" ? member : null} buildTable={buildTable.data} />;
}
